import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { BiMenuAltRight } from "react-icons/bi";
import { IoClose } from "react-icons/io5";
import { Logo } from "./Logo.jsx";
import { motion, AnimatePresence } from "framer-motion";

const LinkTab = ({ name, link, i, activeSection, onActivePosition }) => {
  const isActive = activeSection === link.substring(1);
  const tabRef = useRef(null);

  useEffect(() => {
    if (isActive && tabRef.current) {
      const { offsetLeft: left, offsetWidth: width } = tabRef.current;
      onActivePosition?.({ left, width });
    }
  }, [isActive]);

  return (
    <motion.li
      ref={tabRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: i * 0.05 }}
    >
      <a
        href={link}
        onClick={(e) => {
          e.preventDefault();
          const section = document.querySelector(link);
          if (section) {
            section.scrollIntoView({ behavior: "smooth" });
            window.history.pushState(null, "", link);
          }
        }}
        className={isActive ? "navActive" : ""}
      >
        {name}
      </a>
    </motion.li>
  );
};


const Navbar = ({ themeMode, setThemeMode }) => {
  const links = [
    { name: "HOME", link: "#home" },
    { name: "ABOUT", link: "#about" },
    { name: "PROJECTS", link: "#projects" },
    { name: "CONTACT", link: "#contact" },
  ];

  const [isExpanded, setIsExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const underlineRef = useRef({ left: 0, width: 0 });

  const handleTabPosition = (newPos) => {
    const oldPos = underlineRef.current;
    if (oldPos.left === newPos.left && oldPos.width === newPos.width) return;

    const fromLeft = Math.min(oldPos.left, newPos.left);
    const toRight = Math.max(oldPos.left + oldPos.width, newPos.left + newPos.width);
    const expandedWidth = toRight - fromLeft;

    // Step 1: Expand
    setUnderlineStyle({
      left: fromLeft,
      width: expandedWidth
    });

    // Step 2: After a short delay, snap to the new tab
    setTimeout(() => {
      requestAnimationFrame(() => {
        underlineRef.current = newPos;
        setUnderlineStyle(newPos);
      });
    }, 150);
  };

  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsExpanded(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null, // viewport
      rootMargin: "0px",
      threshold: 0.3, // 60% of section visible
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          if (id) {
            setActiveSection(id);
          }
        }
      });
    };

    const observer = new IntersectionObserver(callback, observerOptions);
    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [location]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 768) {
      setIsExpanded(false);
    }
  };

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.history.pushState(null, '', `#${id}`);
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: "smooth" });
        }, 100); // Slight delay to ensure DOM is ready
      }
    }
  }, [location.hash]);


  return (
    <StyledNavbar
      id="navbar"
      className={scrolled ? "scrolled" : ""}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="navbar-container">
        <div className="nav-left">
          <Logo />
          <div className="nav-toggle" onClick={handleToggle}>
            {isExpanded ?
              <IoClose className="navToggle" /> :
              <BiMenuAltRight className="navToggle" />
            }
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              className="nav-right"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClick}
            >
              <ul className="nav-list">
                {links.map((li, i) => (
                  <LinkTab
                    {...li}
                    key={li.name}
                    i={i}
                    activeSection={activeSection}
                    onActivePosition={handleTabPosition}
                  />
                ))}
                <motion.div
                  className="underline"
                  layout
                  initial={false}
                  animate={underlineStyle}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
                <motion.li
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="theme-toggle-wrapper"
                >
                  {/* <button
                    className="theme-toggle"
                    onClick={() => {
                      setThemeMode(themeMode === "light" ? "dark" : "light");
                    }}
                    aria-label="Toggle dark mode"
                  >
                    {themeMode === "light" ? "DARK" : "LIGHT"}
                  </button> */}
                </motion.li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </StyledNavbar>
  );
};

const StyledNavbar = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0;
  color: var(--primary-text);
  z-index: 1000;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: rgba(var(--primary-background-rgb), 0.7);
  border-bottom: 1px solid transparent;
  
  &.scrolled {
    height: 56px;
    background-color: rgba(var(--primary-background-rgb), 0.85);
    border-bottom: 1px solid rgba(var(--border-color-rgb), 0.1);
    box-shadow: none;
  }
  
  .navbar-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90%;
    max-width: 1200px;
    margin: 0 auto;
    height: 100%;
    position: relative;
  }
  
  .nav-left {
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    .nav-toggle {
      display: none;
      cursor: pointer;
      
      .navToggle {
        font-size: 1.3rem;
        transition: color 0.3s ease;
        
        &:hover {
          color: var(--vibrant);
        }
      }
    }
  }
  .nav-right {
    ul {
      display: flex;
      align-items: center;
      list-style: none;
      gap: 2.5rem;
      margin: 0;
      padding: 0;
      
      li {
        position: relative;
        
        a {
          font-weight: 500;
          font-size: 0.8rem;
          text-decoration: none;
          color: var(--secondary-text);
          padding: 0.5rem 0;
          transition: all 0.3s ease;
          letter-spacing: 0.15em;
          
          &:hover {
            color: var(--primary-text);
          }
          
          span.active {
            position: absolute;
            display: block;
            height: 2px;
            bottom: -4px;
            left: 0;
            width: 100%;
            background: linear-gradient(
              90deg,
              var(--vibrant, #4a5bef) 0%,
              var(--accent-color, #61DAFB) 100%
            );
            border-radius: 1px;
          }
        }
        
        a.navActive {
          color: var(--primary-text);
          /* font-weight: 600; */
        }
      }
      
      .theme-toggle-wrapper {
        margin-left: 1rem;
        
        .theme-toggle {
          font-size: 0.75rem;
          letter-spacing: 0.15em;
          font-weight: 500;
          text-transform: uppercase;
          color: var(--secondary-text);
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          
          &::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 0;
            width: 0;
            height: 2px;
            background: linear-gradient(
              90deg,
              var(--vibrant, #4a5bef) 0%,
              var(--accent-color, #61DAFB) 100%
            );
            transition: width 0.3s ease;
            border-radius: 1px;
          }
          
          &:hover {
            color: var(--primary-text);
            
            &::after {
              width: 100%;
            }
          }
        }
      }
    }
  }

  @media only screen and (max-width: 768px) {
    .navbar-container {
      width: 95%;
    }
    
    .nav-left {
      width: 100%;
      
      .nav-toggle {
        display: block;
      }
    }
    
    .nav-right {
      position: absolute;
      top: 56px;
      left: 0;
      width: 100%;
      background-color: rgba(var(--primary-background-rgb), 0.95);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      padding: 1.5rem 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border: none;
      border-bottom: 1px solid rgba(var(--border-color-rgb), 0.1);
      
      ul {
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
        padding: 0.5rem;
        
        li {
          width: 100%;
          text-align: center;
          
          a {
            display: block;
            padding: 0.5rem 0;
            font-size: 0.85rem;
          }
          
          span.active {
            display: none;
          }
        }
        
        .theme-toggle-wrapper {
          margin: 0.5rem 0 0;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(var(--border-color-rgb), 0.1);
          
          .theme-toggle {
            font-size: 0.8rem;
          }
        }
      }
    }
  }
  .nav-list{
    position: relative;
  }
  .underline {
  position: absolute;
  bottom: -4px;
  height: 2px;
  background: linear-gradient(
              90deg,
              var(--vibrant, #4a5bef) 0%,
              var(--accent-color, #61DAFB) 100%
            );
  /* background: #61DAFB; */
  border-radius: 1px;
}
`;

export default Navbar;